import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { useUnread } from '../context/UnreadContext';
import { NAV_DESTINATIONS } from '../config/navItems';
import { User, Bell, BarChart3, Settings, LogOut, Anchor, BarChart2, Palette, Inbox, Route } from 'lucide-react';

const ACCOUNT_LINKS = [
  { icon: User, label: 'Mon profil', path: '/profile' },
  { icon: Route, label: 'Ma frise de voyage', path: '/frise' },
  { icon: Palette, label: 'Ma cabine', path: '/cabines' },
  { icon: BarChart2, label: 'Mes stats', path: '/my-stats' },
  { icon: Bell, label: 'Notifications', path: '/notifications' },
];

/** Tiroir mobile « Plus » : toutes les destinations + compte + admin. */
const MobileMoreSheet = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const { isAdmin, logout } = useAuth();
  const { total: totalUnread, dmTotal } = useUnread();

  const go = (path) => {
    onOpenChange(false);
    navigate(path);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="h-[88vh] rounded-t-2xl p-0 flex flex-col"
        data-testid="mobile-more-sheet"
      >
        <SheetHeader className="px-5 pt-5 pb-3 text-left shrink-0">
          <SheetTitle className="font-heading flex items-center gap-2 text-lg">
            <Anchor className="w-5 h-5 text-primary" />
            Tout l&apos;Équipage
          </SheetTitle>
        </SheetHeader>
        <div className="filet-gold mx-5 shrink-0" />

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Navigation</p>
            <div className="grid grid-cols-3 gap-2">
              {NAV_DESTINATIONS.map((item) => {
                const Icon = item.icon;
                const badge = item.path === '/channels' ? totalUnread : item.path === '/dm' ? dmTotal : 0;
                return (
                  <button
                    key={item.path}
                    onClick={() => go(item.path)}
                    className="relative min-h-[84px] rounded-xl border border-border bg-card px-2 py-3 flex flex-col items-center justify-center gap-1.5 active:scale-[0.97] transition-transform"
                    data-testid={`more-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <Icon className={`w-5 h-5 ${item.color}`} />
                    <span className="text-[11px] leading-tight text-center">{item.label}</span>
                    {badge > 0 && (
                      <span
                        className="absolute top-1.5 right-1.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center"
                        style={{ background: 'hsl(var(--red-dossier))', color: '#fff' }}
                      >
                        {badge > 99 ? '99+' : badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Mon compte</p>
            <div className="space-y-1">
              {ACCOUNT_LINKS.map((item) => {
                const Icon = item.icon;
                return (
                  <Button
                    key={item.path}
                    variant="ghost"
                    className="w-full justify-start min-h-[48px] text-base"
                    onClick={() => go(item.path)}
                    data-testid={`more-account-${item.path.replace('/', '')}`}
                  >
                    <Icon className="w-5 h-5 mr-3 text-primary" />
                    {item.label}
                  </Button>
                );
              })}
              {isAdmin && (
                <>
                  <Button
                    variant="ghost"
                    className="w-full justify-start min-h-[48px] text-base"
                    onClick={() => go('/passerelle')}
                    data-testid="more-account-passerelle"
                  >
                    <Inbox className="w-5 h-5 mr-3 text-primary" />
                    Passerelle du Capitaine
                  </Button>
                  <Button
                    variant="ghost"
                    className="w-full justify-start min-h-[48px] text-base"
                    onClick={() => go('/admin')}
                    data-testid="more-account-admin"
                  >
                    <Settings className="w-5 h-5 mr-3 text-primary" />
                    Administration
                  </Button>
                  <Button
                    variant="ghost"
                    className="w-full justify-start min-h-[48px] text-base"
                    onClick={() => go('/analytics')}
                    data-testid="more-account-analytics"
                  >
                    <BarChart3 className="w-5 h-5 mr-3 text-primary" />
                    Analytics
                  </Button>
                </>
              )}
              <Button
                variant="ghost"
                className="w-full justify-start min-h-[48px] text-base text-red-600 hover:text-red-700 hover:bg-red-500/10"
                onClick={() => { onOpenChange(false); logout(); }}
                data-testid="more-account-logout"
              >
                <LogOut className="w-5 h-5 mr-3" />
                Se déconnecter
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default MobileMoreSheet;
